import { ApiError } from '@/lib/api/errors.js';
import { t } from '@/lib/i18n/messages.js';
import type { DashboardLayout } from '@quartup/reports-shared';
import { ReportTemplateModelSchema } from '@quartup/reports-shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDashboardDetail } from '../hooks/use-dashboard-detail.js';
import { useExecuteDashboard } from '../hooks/use-execute-dashboard.js';
import type {
  DashboardExecuteResponse,
  WidgetExecuteRequest,
  WidgetResult,
} from '../lib/dashboards-client.js';
import { executeWidget } from '../lib/dashboards-client.js';
import { DashboardGrid } from './dashboard-grid.js';
import { DashboardHeader } from './dashboard-header.js';

// ---------------------------------------------------------------------------
// Loading / error states
// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <div
      className="flex items-center justify-center py-20"
      aria-busy="true"
      aria-label={t('report.loading')}
    >
      <svg
        className="h-8 w-8 animate-spin text-[var(--color-qup-primary)]"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
        />
      </svg>
      <span className="sr-only">{t('report.loading')}</span>
    </div>
  );
}

function DetailLoadError({ error }: { error: Error }) {
  const isNotFound = error instanceof ApiError && error.isNotFound;
  return (
    <div role="alert" className="flex flex-col items-center justify-center py-20 text-center">
      <p className="text-base font-semibold text-[var(--color-qup-text-dark)]">
        {isNotFound ? t('report.not_found') : t('report.error')}
      </p>
      <p className="mt-1 text-sm text-[var(--color-qup-text-muted-dark)]">{error.message}</p>
    </div>
  );
}

function NotADashboard() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <p className="text-base font-semibold text-[var(--color-qup-text-dark)]">
        {t('dashboards.not_a_dashboard')}
      </p>
      <a href="/catalog" className="mt-4 text-sm text-[var(--color-qup-primary)] hover:underline">
        {t('error.go_home')}
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard page
// ---------------------------------------------------------------------------

interface DashboardPageProps {
  tenantReportId: string;
  /** Global params from URL state — defaults to {} */
  globalParams?: Record<string, unknown>;
  /** Per-widget params from URL state — keyed by widgetId */
  widgetParamsMap?: Record<string, Record<string, unknown>>;
}

export function DashboardPage({
  tenantReportId,
  globalParams = {},
  widgetParamsMap = {},
}: DashboardPageProps) {
  const {
    data: detail,
    isLoading: detailLoading,
    error: detailError,
  } = useDashboardDetail(tenantReportId);

  const {
    mutateAsync: executeDashboardAsync,
    isPending: isDashboardExecuting,
    data: bulkResult,
  } = useExecuteDashboard(tenantReportId);

  // Per-widget override state — individual refresh results overlay the bulk result
  const [widgetOverrides, setWidgetOverrides] = useState<Record<string, WidgetResult>>({});
  const [loadingWidgets, setLoadingWidgets] = useState<ReadonlySet<string>>(new Set());

  // D-1: AbortController refs — one per widgetId, keyed by widgetId. Aborts any in-flight request
  // when a new refresh starts for the same widget, or on component unmount.
  const widgetAbortRefs = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    return () => {
      // D-1: abort all in-flight widget requests on unmount
      for (const ctrl of widgetAbortRefs.current.values()) {
        ctrl.abort();
      }
      widgetAbortRefs.current.clear();
    };
  }, []);

  // Derived merged result
  const mergedResult: DashboardExecuteResponse | undefined =
    bulkResult !== undefined
      ? {
          ...bulkResult,
          widgetResults: { ...bulkResult.widgetResults, ...widgetOverrides },
        }
      : undefined;

  // Whether this is the initial mount
  const didMountRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally omits executeDashboardAsync and globalParams — only re-fires on dashboard identity change, not on every param edit
  useEffect(() => {
    if (didMountRef.current) return;
    if (detail === undefined || detail.kind !== 'dashboard') return;
    didMountRef.current = true;
    void executeDashboardAsync({ global_params: globalParams });
  }, [detail?.tenantReportId]);

  const handleRefreshAll = useCallback(() => {
    setWidgetOverrides({});
    void executeDashboardAsync({
      global_params: globalParams,
      fresh: true,
    });
  }, [executeDashboardAsync, globalParams]);

  const handleRefreshWidget = useCallback(
    async (widgetId: string, req: WidgetExecuteRequest) => {
      // D-1: abort any existing in-flight request for this widget before starting a new one
      const existingCtrl = widgetAbortRefs.current.get(widgetId);
      if (existingCtrl !== undefined) {
        existingCtrl.abort();
      }
      const ctrl = new AbortController();
      widgetAbortRefs.current.set(widgetId, ctrl);

      setLoadingWidgets((prev) => new Set([...prev, widgetId]));
      try {
        const res = await executeWidget(tenantReportId, widgetId, req, ctrl.signal);
        const widgetResult: WidgetResult = {
          status: 'success',
          rows: res.rows,
          meta: res.meta,
          semiJoinCount: res.semiJoinCount,
          appliedQuery: res.appliedQuery,
        };
        setWidgetOverrides((prev) => ({ ...prev, [widgetId]: widgetResult }));
      } catch (err) {
        // D-1: ignore AbortError — a new request superseded this one
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const e = err as { message?: string; code?: string };
        const widgetResult: WidgetResult = {
          status: 'error',
          error: {
            code: e.code ?? 'UNKNOWN',
            message: e.message ?? 'Widget execution failed',
            requestId: undefined,
          },
        };
        setWidgetOverrides((prev) => ({ ...prev, [widgetId]: widgetResult }));
      } finally {
        widgetAbortRefs.current.delete(widgetId);
        setLoadingWidgets((prev) => {
          const next = new Set(prev);
          next.delete(widgetId);
          return next;
        });
      }
    },
    [tenantReportId],
  );

  // Early exits
  if (detailLoading) return <LoadingState />;
  if (detailError !== null && detailError !== undefined)
    return <DetailLoadError error={detailError} />;
  if (detail === undefined) return null;
  if (detail.kind !== 'dashboard') return <NotADashboard />;

  // Parse dashboard layout
  const templateParsed = ReportTemplateModelSchema.safeParse(detail.definition);
  if (!templateParsed.success || templateParsed.data.kind !== 'dashboard') {
    return <NotADashboard />;
  }

  const template = templateParsed.data;
  const layout: DashboardLayout = template.layout;
  const titleText = template.title.es ?? template.title.en ?? detail.code;

  return (
    <div className="flex min-h-0 flex-col bg-[var(--color-qup-bg-dark)]">
      <DashboardHeader
        title={titleText}
        executionResult={mergedResult}
        isLoading={isDashboardExecuting}
        onRefreshAll={handleRefreshAll}
      />

      {/* Partial failure banner — uses aria-live instead of role="status" to avoid semantic lint */}
      {mergedResult !== undefined && mergedResult.dashboardMeta.failed > 0 && (
        <div
          className="border-b border-yellow-800/30 bg-yellow-950/20 px-4 py-2"
          aria-live="polite"
          aria-label="partial-failure-banner"
          data-testid="partial-failure-banner"
        >
          <p className="text-sm text-yellow-400">
            {t('dashboards.partial_failure')
              .replace('{failed}', String(mergedResult.dashboardMeta.failed))
              .replace('{total}', String(mergedResult.dashboardMeta.totalWidgets))}
          </p>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <DashboardGrid
          layout={layout}
          executionResult={mergedResult}
          loadingWidgets={loadingWidgets}
          isExecuting={isDashboardExecuting}
          globalParams={globalParams}
          widgetParamsMap={widgetParamsMap}
          onRefreshWidget={handleRefreshWidget}
        />
      </div>
    </div>
  );
}
